import React, { useRef, useState } from "react";
import { IconCheck, IconX, IconPencil, IconRotate2 } from "@tabler/icons-react";
import { PrimaryButton } from "../../Buttons/PrimaryButton";
import { SecondaryButton } from "../../Buttons/SecondaryButton";
import { Spacing } from "../../Spacing/Spacing";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useRegistrants } from "@/providers/RegistrantsProvider";
import { useCompanions } from "@/providers/CompanionsProvider";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { useRole } from "@/providers/RoleProvider";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { AcceptanceStateChip } from "@/components/AcceptanceStateChip/AcceptanceStateChip";
import { RedecideDialog } from "@/components/RedecideDialog/RedecideDialog";

type TableCellContentActionProps = {
  registrant: boolean;
  id: string;
  archived: boolean;
  actionStatus: string;
};

/** Which action is impossible from this state, and the reason to show for it. */
function blockedReason(
  current: AcceptanceState | null,
  target: AcceptanceState,
): string | null {
  if (current === null) return "stateUnknown";
  if (current === target) {
    if (target === AcceptanceState.ACCEPTED) return "alreadyAccepted";
    if (target === AcceptanceState.DECLINED) return "alreadyDeclined";
    return "alreadyOpen";
  }
  // A waitlisted guest's membership is owned by the capacity engine. Reopening
  // one would orphan its waitlist position, so the control is refused rather
  // than guessed at.
  if (current === AcceptanceState.SYSTEM && target === AcceptanceState.PENDING) {
    return "reopenNotAvailableWaitlist";
  }
  return null;
}

export const TableCellContentAction: React.FC<TableCellContentActionProps> = React.memo(
  function TableCellContentAction({ registrant, id, archived, actionStatus }) {
    const registrantContext = useRegistrants();
    const companionContext = useCompanions();
    const { decide } = registrant ? registrantContext! : companionContext!;
    const t = useTranslations("eventGuestsRequests");
    const tErr = useTranslations("errors");

    // TableCellContent is reused by render sites outside the event scope (no
    // CapabilitiesProvider), so this must fail closed there — same reasoning as
    // TCCCheckInOut. The old gate was `legacyRoleFloor !== "VIEWER"`, which
    // collapses to EDITOR on ANY of ~19 unrelated write capabilities: a role
    // that may only edit the website was shown live approve/decline buttons.
    const { can } = useCapabilitiesOptional();
    const { guard } = usePreviewWriteGuard();
    const { isArchived, isPlanLocked } = useRole();

    const [isEditMode, setIsEditMode] = useState(false);
    const [dialogTarget, setDialogTarget] = useState<AcceptanceState | null>(null);
    // A ref as well as state: two clicks inside one tick both read the same
    // state value, so state alone cannot stop a double submit.
    const busyRef = useRef(false);
    const [busy, setBusy] = useState<AcceptanceState | null>(null);

    const state = Object.values(AcceptanceState).includes(actionStatus as AcceptanceState)
      ? (actionStatus as AcceptanceState)
      : null;
    const locked = isArchived || isPlanLocked;
    // Both locks need a reason. `locked` has always covered the plan lock too,
    // but only the archive case produced text — so on a plan-locked event the
    // buttons greyed out with an empty `disabledMessage`, which is exactly the
    // "mystery greyed-out control" this block exists to prevent.
    const lockReason = isArchived
      ? tErr("eventArchivedReadOnly")
      : isPlanLocked
        ? tErr("eventPlanLocked")
        : "";

    if (!can("guests:write")) return <></>;

    const run = async (target: AcceptanceState, retriggerAutomations: boolean) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(target);
      try {
        const outcome = await decide(id, target, { retriggerAutomations });
        // `null` means the write did not go through, or the capacity modal has
        // taken over — either way the provider owns the next message.
        if (!outcome) return;
        // The toast must describe what actually happened. It used to say
        // "approved" for an archive re-decision that announced nothing.
        const kind = !archived
          ? target === AcceptanceState.ACCEPTED
            ? "approved"
            : "declined"
          : target === AcceptanceState.PENDING
            ? "reopened"
            : "redecided";
        window.dispatchEvent(
          new CustomEvent("guestActionToast", {
            detail: {
              kind,
              // Intent AND outcome. The toast needs both: a ticked box that
              // announced nothing must not read as a success, and that is only
              // knowable by comparing the two.
              retriggered: retriggerAutomations,
              triggers: outcome.triggers,
            },
          }),
        );
      } finally {
        busyRef.current = false;
        setBusy(null);
        setDialogTarget(null);
        setIsEditMode(false);
      }
    };

    /** Archive edits ask first; the open tab keeps its one-click flow. */
    const activate = (target: AcceptanceState) => {
      if (archived) setDialogTarget(target);
      else void run(target, false);
    };

    const actionButton = (
      target: AcceptanceState,
      icon: typeof IconCheck,
      label: string,
      borderColor?: string,
      textColor?: string,
    ) => {
      const reason = blockedReason(state, target);
      const disabled = reason !== null || locked || busy !== null;
      return (
        <SecondaryButton
          size="icon"
          text=""
          iconSize={16}
          iconMargin=""
          width="w-max"
          icon={icon}
          ariaLabel={t(label)}
          loading={busy === target}
          disabled={disabled}
          // Without this the greyed-out button is a mystery: the component
          // supports a reason tooltip and simply never passed one. Empty string
          // when enabled, so the tooltip never fires spuriously.
          disabledMessage={reason ? t(reason) : locked ? lockReason : ""}
          borderColor={borderColor}
          textColor={textColor}
          onClick={guard(() => activate(target))}
        />
      );
    };

    return (
      <div className="flex flex-row items-center">
        {archived ? (
          <>
            {isEditMode ? (
              <>
                {actionButton(
                  AcceptanceState.DECLINED,
                  IconX,
                  "actionDecline",
                  "outline-error",
                  "font-error",
                )}
                <Spacing width="w-2" />
                {actionButton(AcceptanceState.ACCEPTED, IconCheck, "actionAccept")}
                <Spacing width="w-2" />
                {actionButton(AcceptanceState.PENDING, IconRotate2, "actionReopen")}
              </>
            ) : (
              <AcceptanceStateChip state={actionStatus} testId="acceptance-state-chip" />
            )}
            <button
              type="button"
              className="p-1 ml-2 rounded-lg hover:surface-quaternary"
              aria-label={isEditMode ? t("actionEditCancel") : t("actionEdit")}
              aria-expanded={isEditMode}
              title={isEditMode ? t("actionEditCancel") : t("actionEdit")}
              data-testid="tcc-action-edit"
              disabled={busy !== null}
              onClick={() => setIsEditMode(!isEditMode)}
            >
              <motion.div
                initial={false}
                animate={{ rotate: isEditMode ? 90 : 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {isEditMode ? (
                  <IconX size={16} className="font-secondary" />
                ) : (
                  <IconPencil size={16} className="font-secondary" />
                )}
              </motion.div>
            </button>
          </>
        ) : (
          <>
            <SecondaryButton
              size="xsmall"
              borderColor="outline-error"
              textColor="font-error"
              text=""
              iconSize={16}
              ariaLabel={t("actionDecline")}
              disabled={locked || busy !== null}
              disabledMessage={locked ? lockReason : ""}
              loading={busy === AcceptanceState.DECLINED}
              onClick={guard(() => activate(AcceptanceState.DECLINED))}
              iconMargin=""
              icon={IconX}
            />
            <Spacing width="w-2" />
            <PrimaryButton
              size="xsmall"
              iconSize={16}
              iconMargin=""
              ariaLabel={t("actionAccept")}
              disabled={locked || busy !== null}
              loading={busy === AcceptanceState.ACCEPTED}
              onClick={guard(() => activate(AcceptanceState.ACCEPTED))}
              icon={IconCheck}
              text=""
            />
          </>
        )}
        {dialogTarget && (
          <RedecideDialog
            registrant={registrant}
            target={dialogTarget}
            current={state}
            isLoading={busy !== null}
            onConfirm={(retrigger) => void run(dialogTarget, retrigger)}
            onCancel={() => setDialogTarget(null)}
          />
        )}
      </div>
    );
  },
);
